import { useCallback, useEffect, useRef, useState } from 'react';
import {
    buildProjectLimitMessage,
    shouldBlockProjectCreation,
    type EntitlementSnapshot,
} from '../entitlements';
import { useSession } from '../SessionContext';
import { useCurrentEntitlements } from './useCurrentEntitlements';
import { canAccessMainApp } from '../../utils/authHelpers';

interface UseProjectCreationGateOptions {
    enabled?: boolean;
}

export function useProjectCreationGate({
    enabled = true,
}: UseProjectCreationGateOptions = {}) {
    const { session, isLoading: isSessionLoading } = useSession();
    const hasAccessToken = Boolean(session?.access_token);
    const canLoadEntitlements = canAccessMainApp(session);
    const shouldLoadEntitlements = enabled && !isSessionLoading && canLoadEntitlements && hasAccessToken;
    const entitlementsQuery = useCurrentEntitlements({ enabled: shouldLoadEntitlements });
    const snapshot = shouldLoadEntitlements
        ? entitlementsQuery.data as EntitlementSnapshot | undefined
        : undefined;
    const isChecking = enabled
        && (isSessionLoading || (shouldLoadEntitlements && entitlementsQuery.isLoading && !snapshot));
    const isUnavailable = shouldLoadEntitlements && !isChecking
        && (!snapshot || snapshot.derived?.entitlement_fallback === true);
    const isBlocked = isUnavailable || shouldBlockProjectCreation(snapshot);
    const [blockedNoticeVisible, setBlockedNoticeVisible] = useState(false);
    const pendingAllowedActionRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        if (!isBlocked) {
            setBlockedNoticeVisible(false);
        }
    }, [isBlocked]);

    useEffect(() => {
        if (!pendingAllowedActionRef.current || isChecking) {
            return;
        }

        const pendingAllowedAction = pendingAllowedActionRef.current;
        pendingAllowedActionRef.current = null;

        if (isBlocked) {
            setBlockedNoticeVisible(true);
            return;
        }

        setBlockedNoticeVisible(false);
        pendingAllowedAction();
    }, [isBlocked, isChecking]);

    const dismissBlockedNotice = useCallback(() => {
        setBlockedNoticeVisible(false);
    }, []);

    const showBlockedNotice = useCallback(() => {
        setBlockedNoticeVisible(true);
    }, []);

    const guardProjectCreation = useCallback((onAllowed: () => void): boolean => {
        if (!enabled) {
            setBlockedNoticeVisible(false);
            onAllowed();
            return true;
        }

        if (isChecking) {
            pendingAllowedActionRef.current = onAllowed;
            return false;
        }

        if (isBlocked) {
            setBlockedNoticeVisible(true);
            return false;
        }

        setBlockedNoticeVisible(false);
        onAllowed();
        return true;
    }, [enabled, isBlocked, isChecking]);

    return {
        snapshot,
        isChecking,
        isUnavailable,
        isBlocked,
        blockedNoticeVisible,
        blockMessage: isBlocked ? buildProjectLimitMessage(snapshot) : null,
        dismissBlockedNotice,
        showBlockedNotice,
        guardProjectCreation,
        entitlementsQuery,
    };
}
